import { useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Slider } from 'antd';

// Same 4:1 ratio as the upload cropper for event banners (UploadImage with crop).
export const BannerAspect = 4 / 1;

// Wide enough for the event page header, small enough to stay well under the upload size limit.
const MaxBannerWidth = 1600;

export interface BannerCropperProps {
    // Must be same-origin or an object URL, so the cropped canvas can be read back.
    src: string;
    onCropComplete: (croppedAreaPixels: Area) => void;
}

/**
 * Lets the admin choose which part of an existing image is shown as event banner - the same
 * choice they get when uploading a new image.
 */
const BannerCropper = (props: BannerCropperProps) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    return <>
        <div style={{ position: "relative", width: "100%", height: "300px", background: "#333", borderRadius: "10px", overflow: "hidden" }}>
            <Cropper
                image={props.src}
                crop={crop}
                zoom={zoom}
                aspect={BannerAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedAreaPixels) => props.onCropComplete(croppedAreaPixels)}
            />
        </div>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={(value: number) => setZoom(value)} />
    </>;
};

/**
 * Draws the chosen area of the image to a canvas and returns it as a JPEG file ready to upload.
 */
export const cropImageToFile = async (src: string, area: Area, fileName: string): Promise<File> => {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

    const width = Math.min(area.width, MaxBannerWidth);
    const height = Math.round(width / BannerAspect);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d")!.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(result => result ? resolve(result) : reject(), "image/jpeg", 0.9);
    });
    return new File([blob], fileName, { type: "image/jpeg" });
};

export default BannerCropper;
